import { FormProvider, useForm } from 'react-hook-form';
import {
  AllUserRes,
  CreateCreditOperation,
  CreateCreditDto,
  UserRole,
} from '../../services/api.types';
import dayjs, { Dayjs } from 'dayjs';
import { Datepicker } from '../Datepicker';
import { Radio } from '../Radio';
import { InputAutocomplete } from '../Inputs/Autocomplete';
import { DataAutocomplete } from '../SelectResponsibles';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { usersService } from '../../services/users.service';
import { Input } from '../Inputs/Input';
import {
  formatMoney,
  formatStringMoney,
  readMoney,
} from '../../utils/formatters';
import { apiService } from '../../services/api';
import { DraggableModal } from '../DraggableModal';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Button } from '../Button';
import { UseControllerProps } from 'react-hook-form';
import { useToast } from '../../hooks';
import { Checkbox } from '../Checkbox';

type ModalFormRules = { [key: string]: UseControllerProps['rules'] };

export interface EditCreditModalProps {
  open: boolean;
  onClose: () => void;
  defaultUser?: DataAutocomplete | null;
  onCreditCreation: () => void;
}

interface EditCreditFormValues {
  receiver: DataAutocomplete | null;
  description: string;
  moneyValue: string;
  creditEditionType: CreateCreditOperation;
  dueDate: dayjs.Dayjs;
  withPaymentBeforeAdd: boolean;
}

export const formRules: ModalFormRules = {
  operationRequired: { required: 'É necessário selecionar o tipo da operação' },
  userRequired: { required: 'É necessário selecionar o usuário' },
  valueRequired: {
    required: 'Valor do crédito adicionado/retirado é obrigatório',
    min: {
      message: 'Valor deve ser no mínimo R$ 0,01',
      value: 0,
    },
  },
  descriptionRequired: {
    required: 'Descrição é obrigatória',
  },
  dueDateRequired: {
    required: 'Data de vencimento é obrigatória',
    validate: (value: Dayjs) => {
      return isNaN(value.toDate().getTime()) ? 'Data inválida' : true;
    },
  },
};

export const EditCreditModal = ({
  open,
  onClose,
  defaultUser,
  onCreditCreation,
}: EditCreditModalProps) => {
  const toast = useToast();
  const [responsibles, setResponsibles] = useState<DataAutocomplete[]>([]);
  const [loading, setIsLoading] = useState<boolean>(false);

  const formDefaultValues: EditCreditFormValues = {
    creditEditionType: CreateCreditOperation.ADD,
    dueDate: dayjs().add(7, 'days'),
    description: '',
    receiver: defaultUser ?? null,
    moneyValue: formatMoney(0),
    withPaymentBeforeAdd: true,
  };

  const eventForm = useForm<EditCreditFormValues>({
    mode: 'all',
    defaultValues: formDefaultValues,
  });

  const creditEditionType = eventForm.watch('creditEditionType');
  const withPaymentBeforeAdd = eventForm.watch('withPaymentBeforeAdd');

  useEffect(() => {
    usersService
      .getAllUsers({ role: UserRole.RESPONSIBLE })
      .then((response: AllUserRes) => {
        setResponsibles(
          response.users.map((user) => ({
            id: user.id,
            displayName: user.fullName,
          }))
        );
      });
  }, []);

  const onSubmit = async (data: EditCreditFormValues) => {
    if (data.receiver?.id) {
      setIsLoading(true);
      const reqData: CreateCreditDto = {
        value: readMoney(data.moneyValue),
        dueDate: data.dueDate.toDate(),
        creditEditionType: data.creditEditionType,
        description: data.description,
        receiverId: data.receiver.id,
        withPaymentBeforeAdd: data.withPaymentBeforeAdd,
      };
      await apiService
        .createOrRemoveCredit(reqData)
        .then(() => {
          toast.success(
            data.creditEditionType === CreateCreditOperation.ADD
              ? 'Crédito adicionado com sucesso'
              : 'Crédito removido com sucesso'
          );
          onCreditCreation();
        })
        .finally(() => setIsLoading(false));
    }
  };

  const editionTypeDescription = useMemo(() => {
    if (creditEditionType === CreateCreditOperation.ADD) {
      return withPaymentBeforeAdd
        ? 'Essa operação vai gerar uma cobrança, e o crédito será adicionado somente após seu pagamento.'
        : 'Essa operação vai adicionar um valor ao saldo de créditos do responsável.';
    }
    if (creditEditionType === CreateCreditOperation.REMOVE)
      return 'Essa operação vai retirar um valor do saldo de créditos do responsável.';
    return '';
  }, [creditEditionType, withPaymentBeforeAdd]);

  const handleChangeMaskMoney = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    eventForm.setValue('moneyValue', formatStringMoney(event.target.value));
  };

  return (
    <DraggableModal open={open} onClose={onClose} title="Editar crédito">
      <FormProvider {...eventForm}>
        <form
          onSubmit={eventForm.handleSubmit(onSubmit)}
          className="w-full flex flex-col items-center justify-between p-6 gap-4"
        >
          <div className="w-full max-w-sm flex flex-col gap-4 p-4">
            <div className="flex flex-col gap-0">
              <Radio
                label="Operação"
                direction="row"
                name="creditEditionType"
                rules={formRules.operationRequired}
                defaultValue={CreateCreditOperation.ADD}
                options={[
                  {
                    label: 'Adicionar',
                    value: CreateCreditOperation.ADD,
                  },
                  {
                    label: 'Retirar',
                    value: CreateCreditOperation.REMOVE,
                  },
                ]}
              />

              {creditEditionType === CreateCreditOperation.ADD && (
                <Checkbox
                  name="withPaymentBeforeAdd"
                  label="Gerar cobrança antes de adicionar o crédito?"
                />
              )}

              <p>{editionTypeDescription}</p>
            </div>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale="pt-br"
            >
              {creditEditionType === CreateCreditOperation.ADD &&
                withPaymentBeforeAdd && (
                  <Datepicker
                    labelOut={false}
                    name="dueDate"
                    label="Data de vencimento"
                    disablePast
                    rules={formRules.dueDateRequired}
                  />
                )}
            </LocalizationProvider>
            <InputAutocomplete
              name="receiver"
              label="Usuário destino"
              CustomOnChange={(_, value) => {
                eventForm.setValue('receiver', value);
              }}
              values={responsibles}
              rules={formRules.userRequired}
            />

            <Input
              name="moneyValue"
              label={`Valor a ${
                creditEditionType === CreateCreditOperation.ADD
                  ? 'adicionar'
                  : 'remover'
              }`}
              onChange={handleChangeMaskMoney}
              rules={formRules.valueRequired}
            />

            <Input
              name="description"
              type="text"
              label="Descrição"
              rules={formRules.descriptionRequired}
            />
          </div>
          <Button
            isLoading={loading}
            type="submit"
            className="w-6/12 mt-6"
            circular
          >
            {CreateCreditOperation.ADD === creditEditionType
              ? 'Criar'
              : 'Remover'}
          </Button>
        </form>
      </FormProvider>
    </DraggableModal>
  );
};
